/**
 * Journal du client.
 *
 * Tout le jeu passe par ici. Les entrees sont mises en tampon puis envoyees au
 * serveur par lots, qui les ecrit dans le fichier de session. On capture aussi
 * les erreurs non gerees et les appels a console.error, pour qu'une exception
 * pendant une partie se retrouve toujours dans le fichier.
 *
 *   journal.info('boot', 'demarrage')
 *   journal.input('tap', 'clic sol', { x: 12.4, z: 88.1 })
 *   journal.warn('perf', 'fps 41')
 */
import { LOG_BUFFER_MAX, LOG_FLUSH_MS, LOG_PERF_MS, FPS_WARN } from '../../shared/constants.ts'
import type { LogEntry, LogLevel, SessionMeta, SessionOuverte } from '../../shared/types/log.ts'

/**
 * Les vraies fonctions de la console, capturees au chargement du module.
 *
 * On remplace plus bas `console.warn` et `console.error` pour recopier leurs
 * appels dans le journal. Si le journal se servait ensuite de `console.warn`
 * pour son propre affichage, il rappellerait sa propre substitution: recursion
 * infinie et tampon qui gonfle sans fin. On garde donc les originales.
 */
const CONSOLE = {
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
}

/** Vrai pendant qu'on traite un appel a console.*, pour ne pas boucler. */
let dansConsole = false

let sessionId: string | null = null
let fichier: string | null = null
let tampon: LogEntry[] = []
let minuterie: number | null = null
let envoiEnCours = false
/** Vrai quand le serveur est injoignable: on garde tout et on reessaie. */
let horsLigne = false
/** Nom et token memorises pour pouvoir rouvrir une session apres un 409. */
let identite: { nom: string; token: string } | null = null

function ajouter(lvl: LogLevel, tag: string, msg: string, data?: unknown): void {
  const e: LogEntry = { t: Date.now(), lvl, tag, msg }
  if (data !== undefined) e.data = data
  tampon.push(e)

  // Refleter sur la console du navigateur pour le debogage immediat, via les
  // fonctions d'origine: passer par les substitutions rebouclerait ici.
  if (import.meta.env?.DEV) {
    const ligne = `${lvl} [${tag}] ${msg}`
    if (lvl === 'ERROR') CONSOLE.error(ligne, data ?? '')
    else if (lvl === 'WARN') CONSOLE.warn(ligne, data ?? '')
    else if (lvl !== 'DEBUG') CONSOLE.log(ligne, data ?? '')
  }

  if (tampon.length >= LOG_BUFFER_MAX) void vider()
}

export const journal = {
  debug(tag: string, msg: string, data?: unknown): void {
    ajouter('DEBUG', tag, msg, data)
  },
  /** Reserve aux interactions de l'utilisateur: clic, glissement, menu. */
  input(tag: string, msg: string, data?: unknown): void {
    ajouter('INPUT', tag, msg, data)
  },
  info(tag: string, msg: string, data?: unknown): void {
    ajouter('INFO', tag, msg, data)
  },
  warn(tag: string, msg: string, data?: unknown): void {
    ajouter('WARN', tag, msg, data)
  },
  error(tag: string, msg: string, data?: unknown): void {
    ajouter('ERROR', tag, msg, data)
  },
}

/** Nom du fichier de session en cours, pour l'afficher dans l'interface. */
export function fichierSession(): string | null {
  return fichier
}

async function vider(): Promise<void> {
  if (envoiEnCours || tampon.length === 0 || !sessionId) return
  envoiEnCours = true
  const lot = tampon
  tampon = []
  try {
    const r = await fetch('/api/log', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ sessionId, entries: lot }),
      keepalive: true,
    })
    if (r.status === 409) {
      // Le serveur a redemarre: on rouvre une session et on reenvoie le lot.
      tampon = lot.concat(tampon)
      sessionId = null
      if (identite) await demarrerJournal(identite.nom, identite.token, true)
    } else if (!r.ok) {
      tampon = lot.concat(tampon)
      if (!horsLigne) {
        horsLigne = true
        console.warn(`journal: le serveur repond ${r.status}, les lignes sont gardees en memoire`)
      }
    } else if (horsLigne) {
      horsLigne = false
      console.log('journal: reconnecte')
    }
  } catch {
    tampon = lot.concat(tampon)
    if (!horsLigne) {
      horsLigne = true
      console.warn('journal: serveur injoignable, les lignes sont gardees en memoire')
    }
  } finally {
    // Eviter que le tampon grossisse sans fin si le serveur reste muet.
    if (tampon.length > LOG_BUFFER_MAX * 8) tampon = tampon.slice(-LOG_BUFFER_MAX * 4)
    envoiEnCours = false
  }
}

function infosGl(): SessionMeta['gl'] {
  try {
    const toile = document.createElement('canvas')
    const gl = toile.getContext('webgl2') ?? toile.getContext('webgl')
    if (!gl) return undefined
    const info = gl.getExtension('WEBGL_debug_renderer_info')
    return {
      renderer: info ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) : String(gl.getParameter(gl.RENDERER)),
      vendor: info ? String(gl.getParameter(info.UNMASKED_VENDOR_WEBGL)) : String(gl.getParameter(gl.VENDOR)),
      version: String(gl.getParameter(gl.VERSION)),
    }
  } catch {
    return undefined
  }
}

/**
 * Ouvre la session de journalisation. A appeler des que le nom du joueur est
 * connu; les lignes emises avant sont conservees et envoyees ensuite.
 */
export async function demarrerJournal(nom: string, token: string, silencieux = false): Promise<void> {
  identite = { nom, token }
  const meta: SessionMeta = {
    nom,
    token,
    userAgent: navigator.userAgent,
    langue: navigator.language,
    ecran: { w: window.screen.width, h: window.screen.height, dpr: window.devicePixelRatio },
    gl: infosGl(),
    coeurs: navigator.hardwareConcurrency,
    memoire: (navigator as { deviceMemory?: number }).deviceMemory,
  }
  try {
    const r = await fetch('/api/session', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(meta),
    })
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    const s = (await r.json()) as SessionOuverte
    sessionId = s.sessionId
    fichier = s.fichier
    if (!silencieux) {
      journal.info('journal', `session ouverte dans logs/${s.fichier}`, {
        decalageHorloge: s.tServeur - Date.now(),
      })
    }
  } catch (e) {
    console.warn('journal: impossible d ouvrir la session', e)
    horsLigne = true
  }

  if (minuterie === null) {
    minuterie = window.setInterval(() => void vider(), LOG_FLUSH_MS)
    installerFilets()
    installerPerf()
  }
  void vider()
}

/** Envoie ce qui reste et ferme la session. */
export function arreterJournal(raison: string): void {
  if (!sessionId) return
  journal.info('journal', `fin de session (${raison})`)
  const corps = JSON.stringify({ sessionId, entries: tampon })
  tampon = []
  // sendBeacon survit a la fermeture de l'onglet.
  const envoye = navigator.sendBeacon?.('/api/session/fin', new Blob([corps], { type: 'text/plain' }))
  if (!envoye) {
    void fetch('/api/session/fin', { method: 'POST', body: corps, keepalive: true }).catch(() => {})
  }
}

let filetsInstalles = false

function installerFilets(): void {
  if (filetsInstalles) return
  filetsInstalles = true

  window.addEventListener('error', (ev) => {
    journal.error('erreur', ev.message, {
      fichier: ev.filename,
      ligne: ev.lineno,
      colonne: ev.colno,
      stack: ev.error instanceof Error ? ev.error.stack : undefined,
    })
  })

  window.addEventListener('unhandledrejection', (ev) => {
    const r = ev.reason
    journal.error('promesse', r instanceof Error ? r.message : String(r), {
      stack: r instanceof Error ? r.stack : undefined,
    })
  })

  // Intercepter console.error et console.warn sans les faire disparaitre. Le
  // verrou `dansConsole` empeche toute reentrance si quelque chose, dans la
  // chaine de journalisation, ecrit lui-meme sur la console.
  console.error = (...args: unknown[]) => {
    if (!dansConsole) {
      dansConsole = true
      try {
        ajouter('ERROR', 'console', abreger(args.map(texte).join(' ')))
      } finally {
        dansConsole = false
      }
    }
    CONSOLE.error(...args)
  }
  console.warn = (...args: unknown[]) => {
    if (!dansConsole) {
      dansConsole = true
      try {
        ajouter('WARN', 'console', abreger(args.map(texte).join(' ')))
      } finally {
        dansConsole = false
      }
    }
    CONSOLE.warn(...args)
  }

  document.addEventListener('visibilitychange', () => {
    journal.info('onglet', document.hidden ? 'onglet masque' : 'onglet visible')
    if (document.hidden) void vider()
  })

  // Volontairement PAS de gestionnaire `pagehide` ici: client/main.ts en pose
  // un qui sauvegarde la partie PUIS ferme le journal, dans cet ordre. En
  // ajouter un seul ici enverrait deux balises de fin.
}

/** Longueur maximale d'un message recopie depuis la console. */
const MSG_MAX = 4000

function abreger(s: string): string {
  return s.length > MSG_MAX ? `${s.slice(0, MSG_MAX)}…(${s.length} caracteres)` : s
}

function texte(v: unknown): string {
  if (typeof v === 'string') return v
  if (v instanceof Error) return `${v.message}\n${v.stack ?? ''}`
  try {
    return JSON.stringify(v) ?? String(v)
  } catch {
    return String(v)
  }
}

// -------------------------------------------------------------- performance

interface EchantillonPerf {
  fps: number
  draws: number
  tris: number
  memoireGeo?: number
  memoireTex?: number
}

let dernierPerf: EchantillonPerf | null = null

/** Le rendu appelle ceci chaque frame; on n'ecrit qu'un resume periodique. */
export function noterPerf(e: EchantillonPerf): void {
  dernierPerf = e
}

function installerPerf(): void {
  window.setInterval(() => {
    if (!dernierPerf) return
    const e = dernierPerf
    const msg = `fps ${e.fps.toFixed(0)} · appels ${e.draws} · triangles ${(e.tris / 1000).toFixed(0)}k`
    if (e.fps < FPS_WARN) journal.warn('perf', msg, e)
    else journal.debug('perf', msg, e)
  }, LOG_PERF_MS)
}
